import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardSignature } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import TraegerFormPanel from "@/components/TraegerFormPanel";

const KINDS = [["client", "AVGS-Gutschein (Trägerbestätigung)"], ["participant", "Bildungsgutschein (Weiterbildung)"]];
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

const Step = ({ n, title, children, testId }) => (
  <section className="border border-slate-200 bg-white" data-testid={testId}>
    <div className="px-5 py-3 border-b border-slate-200 flex items-baseline gap-3 bg-slate-50">
      <span className="font-heading text-lg font-light text-slate-400">{n}</span>
      <span className="text-sm font-medium text-slate-900">{title}</span>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

function PersonPicker({ kind, value, onChange }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get(`/traeger-forms/picker/${kind}`).then((r) => setList(r.data)).catch(err); }, [kind]);
  const open = list.filter((p) => !["sent", "printed"].includes(p.form_status.code));
  const shown = useMemo(() => list.filter((p) => `${p.name} ${p.number}`.toLowerCase().includes(q.toLowerCase())), [list, q]);
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder Nummer suchen" className="input-tech" data-testid="tb-person-search" />
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-tech" data-testid="tb-person-select">
        <option value="">– Kunde wählen ({shown.length}) –</option>
        {shown.map((p) => <option key={p.id} value={p.id}>{p.name}{p.number ? ` · ${p.number}` : ""} — {p.form_status.label}{p.has_contract ? "" : " · Vertrag fehlt"}</option>)}
      </select>
      {list.length > 0 && (
        <div className="sm:col-span-2 text-xs text-slate-500" data-testid="tb-open-count">
          {open.length ? <><b className="text-red-700">{open.length}</b> von {list.length} Kunden: Trägerbestätigung bzw. Bildungsgutschein noch nicht gedruckt oder gesendet.</> : "Alle Kunden haben eine Trägerbestätigung."}
        </div>
      )}
    </div>
  );
}

const STATUS_CLS = { missing: "text-red-700 bg-red-50", failed: "text-red-700 bg-red-50", progress: "text-amber-800 bg-amber-50", printed: "text-sky-800 bg-sky-50", sent: "text-emerald-800 bg-emerald-50" };

export function TraegerStatusBadge({ status, testId }) {
  return <span className={`px-2 py-0.5 text-[11px] font-medium ${STATUS_CLS[status.code] || ""}`} data-testid={testId}>{status.label}</span>;
}

const Info = ({ label, value }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-sm text-slate-900">{value || "—"}</div></div>
);

function AkteStep({ st }) {
  if (!st) return <div className="text-xs text-slate-400">Lädt…</div>;
  const a = st.akte;
  const STATUS = { pending_approval: "wartet auf Freigabe", sent_for_signature: "zur Unterschrift versendet", signed: "unterschrieben" };
  return (
    <div className="space-y-4" data-testid="tb-akte">
      <div className="text-xs text-slate-500">Diese Daten aus der Kundenakte werden automatisch eingetragen. Änderungen bitte in der Kundenakte vornehmen.</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Info label="Maßnahme" value={a.measure_name} />
        <Info label="Maßnahmennummer" value={a.measure_number} />
        <Info label="Beginn – Ende" value={a.start && a.end ? `${a.start} – ${a.end}` : ""} />
        <Info label="Gesamtdauer" value={a.duration} />
        <Info label="Unterrichtseinheiten" value={a.ue ? `${a.ue}`.replace(/\s*UE\s*$/i, "") + " UE" : ""} />
        <Info label="Gesamtkosten" value={a.price} />
        <Info label="Module" value={a.modules.join(", ")} />
        <Info label="Vertrag" value={st.contract ? STATUS[st.contract.status] || st.contract.status : "noch nicht erstellt"} />
      </div>
      {st.deviations.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="tb-deviations">
          <div className="font-medium mb-1">Weicht vom unterschriebenen Vertrag ab:</div>
          {st.deviations.map((d) => <div key={d.label}>{d.label}: Akte <b>{d.akte}</b> · Vertrag <b>{d.vertrag}</b></div>)}
        </div>
      )}
      {st.upload && (
        <div className="text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="tb-upload-log">
          Vordruck „{st.upload.filename}“ hochgeladen am {new Date(st.upload.created_at).toLocaleString("de-DE")}{st.upload.created_by_name ? ` von ${st.upload.created_by_name}` : ""}
          {st.upload.exported_at && ` · zuletzt gedruckt/heruntergeladen am ${new Date(st.upload.exported_at).toLocaleString("de-DE")}`}
          {st.upload.sent_log.filter((x) => x.ok).map((x, i) => <span key={i}> · gesendet am {new Date(x.at).toLocaleString("de-DE")} an {x.to}</span>)}
        </div>
      )}
    </div>
  );
}

export default function TraegerbestaetigungPage() {
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") === "participant" ? "participant" : "client";
  const pid = params.get("id") || "";
  const [st, setSt] = useState(null);
  const loadSt = () => api.get(`/traeger-forms/status/${kind}/${pid}`).then((r) => setSt(r.data)).catch(err);
  useEffect(() => { setSt(null); if (pid) loadSt(); }, [kind, pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, id) => setParams(id ? { kind: k, id } : { kind: k });
  return (
    <div className="px-4 sm:px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto space-y-5" data-testid="traegerbestaetigung-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5"><ClipboardSignature size={12} /> Verwaltung</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter text-slate-900 mt-1">Trägerbestätigung</h1>
        <p className="text-sm text-slate-500 mt-1">1. Art wählen · 2. Kunde wählen · 3. die eigene Trägerbestätigung bzw. den Bildungsgutschein des Kunden hochladen. Das System füllt alles aus Kundenakte, Maßnahme und Trägerprofil aus – Live-Vorschau prüfen, dann herunterladen, speichern oder an Kunden/Jobcenter senden.</p>
      </div>
      <Step n="1" title="Art und Kunde" testId="tb-step-person">
        <div className="flex gap-2 mb-4" data-testid="tb-kind-toggle">
          {KINDS.map(([k, l]) => (
            <button key={k} onClick={() => set(k)} data-testid={`tb-kind-${k}`}
              className={`px-4 py-2 text-sm border transition-colors ${kind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <PersonPicker kind={kind} value={pid} onChange={(id) => set(kind, id)} />
      </Step>
      {pid && (
        <>
          <Step n="2" title={<span className="flex items-center gap-3">Daten aus der Kundenakte {st && <TraegerStatusBadge status={st.status} testId="tb-status-badge" />}</span>} testId="tb-step-akte">
            <AkteStep st={st} />
          </Step>
          {st && (
            <Step n="3" title={`${kind === "client" ? "Trägerbestätigung" : "Bildungsgutschein"} des Kunden hochladen, Live-Vorschau prüfen, herunterladen oder senden`} testId="tb-step-form">
              <TraegerFormPanel key={`${kind}-${pid}`} kind={kind} personId={pid} onChanged={loadSt} />
            </Step>
          )}
        </>
      )}
    </div>
  );
}
